"use client"

import { createContext, useContext, useMemo } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
  getWorkScopeBySlug,
  getWorkScopeCompletePhotos,
  getWorkScopeSlugCatalog,
  PHOTO_QUERY_KEYS,
  untagWorkScopeComplete,
  WorkScopeCompletePhotosForScope,
} from "@/features/photos/api/photos.api"

type WorkScopeCompletePhotosContextValue = {
  homeId: number
  photosByWorkScopeId: Record<string, WorkScopeCompletePhotosForScope>
  slugToId: ReadonlyMap<string, number>
  isReady: boolean
  removeTag: (photoId: number) => Promise<{ success: boolean; message?: string }>
}

const WorkScopeCompletePhotosContext = createContext<WorkScopeCompletePhotosContextValue | null>(null)

export function WorkScopeCompletePhotosProvider({
  homeId,
  children,
}: {
  homeId: number
  children: React.ReactNode
}) {
  const queryClient = useQueryClient()

  const photosQuery = useQuery({
    queryKey: PHOTO_QUERY_KEYS.workScopeComplete(homeId),
    queryFn: async () => {
      const response = await getWorkScopeCompletePhotos(homeId)
      if (!response.success) {
        throw new Error(response.message || "Failed to load Work Scope Complete photos")
      }
      return response.data ?? {}
    },
  })

  const catalogQuery = useQuery({
    queryKey: PHOTO_QUERY_KEYS.workScopeCatalog,
    queryFn: async () => {
      const response = await getWorkScopeSlugCatalog()
      if (!response.success) {
        throw new Error(response.message || "Failed to load work scopes")
      }
      return response.data ?? []
    },
  })

  const slugToId = useMemo(() => {
    const map = new Map<string, number>()
    for (const scope of catalogQuery.data ?? []) {
      if (scope.workScope) map.set(scope.workScope, scope.id)
    }
    return map
  }, [catalogQuery.data])

  const value = useMemo<WorkScopeCompletePhotosContextValue>(() => ({
    homeId,
    photosByWorkScopeId: photosQuery.data ?? {},
    slugToId,
    isReady: photosQuery.isSuccess && catalogQuery.isSuccess,
    removeTag: async (photoId: number) => {
      const response = await untagWorkScopeComplete(photoId)
      if (!response.success) {
        return { success: false, message: response.message }
      }
      await queryClient.invalidateQueries({ queryKey: PHOTO_QUERY_KEYS.byHome(homeId) })
      await queryClient.invalidateQueries({ queryKey: PHOTO_QUERY_KEYS.workScopeComplete(homeId) })
      await queryClient.invalidateQueries({ queryKey: PHOTO_QUERY_KEYS.workScopeOptions(homeId) })
      return { success: true }
    },
  }), [
    homeId,
    photosQuery.data,
    photosQuery.isSuccess,
    catalogQuery.isSuccess,
    slugToId,
    queryClient,
  ])

  return (
    <WorkScopeCompletePhotosContext.Provider value={value}>
      {children}
    </WorkScopeCompletePhotosContext.Provider>
  )
}

export function useWorkScopeCompletePhotosContext() {
  return useContext(WorkScopeCompletePhotosContext)
}

export function useResolvedWorkScopeId(slug: string): number | null {
  const context = useWorkScopeCompletePhotosContext()
  const knownId = context?.slugToId.get(slug) ?? null
  const fallback = useQuery({
    queryKey: ["work-scopes", "by-name", slug],
    queryFn: async () => {
      const response = await getWorkScopeBySlug(slug)
      if (!response.success) {
        throw new Error(response.message || "Failed to resolve work scope")
      }
      return response.data
    },
    enabled: !!context && context.isReady && knownId == null,
  })

  if (knownId != null) return knownId
  return fallback.data?.id ?? null
}
